import { useMutation, useQueryClient } from '@tanstack/react-query';
import { VonqContractPostingRequirements } from 'api-clients/monolith/models/VonqContractPostingRequirements';
import { useIntl } from 'react-intl';

import { validateSourcingPurchases } from '@/api-clients/validate-sourcing-purchases';
import { useToaster } from '@/components/base/toaster';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { stripVonq } from '@/utils/strip-vonq';
import { shouldShowField } from '../posting-requirements/display-rules';
import { buildLoadedOptionsFromCache } from '../posting-requirements/use-autocomplete-options';

interface ValidatePostingRequirementsInput {
  openingId: string;
  contractId: string;
  /** The channel's form-state key (Vonq product ID). */
  channelKey: string;
}

// Field types whose empty state is an empty array rather than empty string.
const ARRAY_TYPES = new Set(['MULTIPLE', 'TEXTEXPAND']);
// Field types that carry no user value and must never be sent to the API.
const NON_INPUT_TYPES = new Set(['STATISCH', 'QUESTIONNAIRE']);

export function useValidatePostingRequirements() {
  const intl = useIntl();
  const { notifyError } = useToaster();
  const { hookForm } = useJobPostingContext();
  const { getValues, setError, clearErrors } = hookForm;
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: validateSourcingPurchases,
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage:
            'Could not validate posting requirements. Please try again.',
          description:
            'Error toast shown when the server-side posting requirements validation request fails',
        }),
      });
    },
  });

  /**
   * Calls the server-side validate endpoint for a single Vonq contract, maps
   * any returned field-level errors onto the corresponding RHF paths via
   * setError, and returns whether the contract's posting requirements are valid.
   */
  const validatePostingRequirementsAsync = async ({
    openingId,
    contractId,
    channelKey,
  }: ValidatePostingRequirementsInput): Promise<boolean> => {
    const channelValues = getValues(`channels.${channelKey}`);
    const rawRequirements = (getValues(
      `jobDetails.${openingId}.vonqPostingRequirements.${contractId}`,
    ) ?? {}) as Record<string, string | string[] | null | undefined>;

    const defs = getValues(
      `jobDetails.${openingId}.vonqPostingRequirementsDefs`,
    ) as VonqContractPostingRequirements[] | undefined;
    const contractDef = defs?.find(d => d.contract_id === contractId);
    const contractFields = contractDef?.posting_requirements ?? [];
    const loadedOptions = contractDef
      ? buildLoadedOptionsFromCache(contractDef, queryClient)
      : {};

    // Build the payload: only send fields that are currently visible per
    // display rules. Required visible fields are always included (with their
    // current value, preserving the form's empty state of '' or []). Optional
    // visible fields are included only when the user has filled them in.
    const postingRequirements: Record<string, string | string[]> = {};
    for (const field of contractFields) {
      if (NON_INPUT_TYPES.has(field.type)) continue;
      if (
        !shouldShowField(field, rawRequirements, contractFields, loadedOptions)
      )
        continue;

      const value = rawRequirements[field.name];
      const isFilled = Array.isArray(value)
        ? value.length > 0
        : value != null && String(value).trim() !== '';

      if (field.required) {
        // Always send required visible fields; fall back to the correct empty
        // state for the type if the value is somehow absent.
        postingRequirements[field.name] = isFilled
          ? (value as string | string[])
          : ARRAY_TYPES.has(field.type)
            ? []
            : '';
      } else if (isFilled) {
        postingRequirements[field.name] = value as string | string[];
      }
    }

    // Clear any stale server errors for this contract before re-validating.
    clearErrors(
      `jobDetails.${openingId}.vonqPostingRequirements.${contractId}`,
    );

    const response = await mutateAsync({
      channel_slug: channelValues?.slug ?? '',
      start_date: channelValues?.startsAt ?? '',
      end_date: (() => {
        if (channelValues?.endAt) return channelValues.endAt;
        if (channelValues?.startsAt) {
          const d = new Date(channelValues.startsAt);
          d.setDate(d.getDate() + 7);
          return d.toISOString().slice(0, 10);
        }
        return null;
      })(),
      opening_ids: [openingId],
      is_vonq_channel: true,
      vonq_product_id: channelValues?.vonqDetails?.productId ?? null,
      vonq_contract_id: contractId,
      remote_channel_id:
        channelValues?.vonqDetails?.channelId != null
          ? String(channelValues.vonqDetails.channelId)
          : null,
      posting_requirements: postingRequirements,
    });

    const openingIssues = response.vonq_posting_issues?.[openingId];
    if (!openingIssues) return true;

    let hasErrors = false;

    // Map per-field errors from all ordered product specs.
    for (const spec of openingIssues.orderedProductsSpecs ?? []) {
      if (!spec.posting_requirements) continue;
      for (const [fieldName, message] of Object.entries(
        spec.posting_requirements,
      )) {
        setError(
          `jobDetails.${openingId}.vonqPostingRequirements.${contractId}.${fieldName}`,
          { message: stripVonq(message) },
        );
        hasErrors = true;
      }
    }

    // Map targetGroup field errors (keyed by field name → error message).
    for (const [fieldName, message] of Object.entries(
      openingIssues.targetGroup ?? {},
    )) {
      setError(
        `jobDetails.${openingId}.vonqPostingRequirements.${contractId}.${fieldName}`,
        { message: stripVonq(message) },
      );
      hasErrors = true;
    }

    return !hasErrors;
  };

  /**
   * Validates all Vonq contracts associated with an opening in parallel.
   * Returns true only if every contract passes server-side validation.
   */
  const validateAllContractsForOpeningAsync = async (
    openingId: string,
  ): Promise<boolean> => {
    const channels = getValues('channels');
    const vonqChannelEntries = Object.entries(channels ?? {}).filter(
      (
        entry,
      ): entry is [
        string,
        (typeof channels)[string] & {
          vonqDetails: NonNullable<(typeof channels)[string]['vonqDetails']> & {
            contractId: string;
          };
        },
      ] => {
        const ch = entry[1];
        return ch?.vonqDetails != null && ch.vonqDetails.contractId != null;
      },
    );

    if (vonqChannelEntries.length === 0) return true;

    const results = await Promise.allSettled(
      vonqChannelEntries.map(([channelKey, ch]) =>
        validatePostingRequirementsAsync({
          openingId,
          contractId: ch.vonqDetails.contractId,
          channelKey,
        }),
      ),
    );

    return results.every(r => r.status === 'fulfilled' && r.value === true);
  };

  return {
    validatePostingRequirementsAsync,
    validateAllContractsForOpeningAsync,
    isValidatingPostingRequirements: isPending,
  };
}
